import { useEffect, useState } from "react";
import AdminPanelPage from "./Page/AdminPanelPage";
import AuthPage from "./Page/AuthPage";
import InformationPage from "./Page/InformationPage";
import QuizesPage from "./Page/QuizesPage";
import QuizAuthorPage from "./Page/QuizAuthorPage";
import TodayScoresPage from "./Page/TodayScoresPage";
import UserPanelPage from "./Page/UserPanelPage";
import translations from "./data/translations";
import { apiUrl, readApiJson } from "./utils/api";
import "./style/Quiz.css";

const AUTH_API_URL = apiUrl("auth.php");
const PRESENCE_API_URL = apiUrl("presence.php");

function App() {
    const [theme, setTheme] = useState(() => {
        const savedTheme = window.localStorage.getItem("quiz-theme");
        return savedTheme === "dark" ? "dark" : "light";
    });
    const [language, setLanguage] = useState(() => (
        window.localStorage.getItem("quiz-language") === "ar" ? "ar" : "en"
    ));
    const [user, setUser] = useState(null);
    const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
    const [isUserPanelOpen, setIsUserPanelOpen] = useState(false);
    const [isQuizAuthorPageOpen, setIsQuizAuthorPageOpen] = useState(false);
    const [isTodayScoresPageOpen, setIsTodayScoresPageOpen] = useState(false);
    const [informationPage, setInformationPage] = useState(null);
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const [sessionError, setSessionError] = useState("");

    useEffect(() => {
        window.localStorage.setItem("quiz-theme", theme);
    }, [theme]);

    useEffect(() => {
        window.localStorage.setItem("quiz-language", language);
    }, [language]);

    useEffect(() => {
        if (!user) {
            return undefined;
        }

        const updatePresence = async () => {
            try {
                const response = await fetch(PRESENCE_API_URL, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: "{}"
                });
                if (!response.ok) {
                    throw new Error(`Presence API returned HTTP ${response.status}`);
                }
            } catch (error) {
                console.error("Failed to update user presence:", error);
            }
        };

        updatePresence();
        const intervalId = window.setInterval(updatePresence, 60_000);
        return () => window.clearInterval(intervalId);
    }, [user]);

    useEffect(() => {
        const controller = new AbortController();
        fetch(AUTH_API_URL, { signal: controller.signal })
            .then(async (response) => {
                const result = await readApiJson(response, "Authentication API");
                if (!response.ok) {
                    throw new Error(result.error || `Authentication API returned HTTP ${response.status}`);
                }
                setUser(result.authenticated ? result.user : null);
            })
            .catch((error) => {
                if (error.name !== "AbortError") {
                    console.error("Failed to restore authentication session:", error);
                    setSessionError(error.message);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsCheckingSession(false);
                }
            });

        return () => controller.abort();
    }, []);

    const toggleTheme = () => {
        setTheme((currentTheme) => currentTheme === "light" ? "dark" : "light");
    };

    const toggleLanguage = () => {
        setLanguage((currentLanguage) => currentLanguage === "en" ? "ar" : "en");
    };
    const text = translations[language];

    const handleLogout = async () => {
        try {
            const response = await fetch(AUTH_API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "logout" })
            });
            if (!response.ok) {
                throw new Error(`Logout failed with HTTP ${response.status}`);
            }
            setUser(null);
            setIsAdminPanelOpen(false);
            setIsUserPanelOpen(false);
            setIsQuizAuthorPageOpen(false);
            setIsTodayScoresPageOpen(false);
            setInformationPage(null);
        } catch (error) {
            console.error("Logout failed:", error);
            setSessionError(text.logoutError);
        }
    };

    return (
        <div
            className="app-shell"
            data-theme={theme}
            lang={language}
            dir={language === "ar" ? "rtl" : "ltr"}
        >
            <nav className="app-navbar" aria-label={text.mainNavigation}>
                <div className="navbar-brand">
                    {user ? (
                        <button
                            className="navbar-brand-button"
                            type="button"
                            onClick={() => {
                                setIsAdminPanelOpen(false);
                                setIsUserPanelOpen(false);
                                setIsQuizAuthorPageOpen(false);
                                setIsTodayScoresPageOpen(false);
                                setInformationPage(null);
                            }}
                        >
                            <span className="navbar-brand-mark" aria-hidden="true">Q</span>
                            <span>Qouiz</span>
                        </button>
                    ) : (
                        <span className="navbar-brand-name">
                            <span className="navbar-brand-mark" aria-hidden="true">Q</span>
                            <span>Qouiz</span>
                        </span>
                    )}
                </div>
                {user && (
                    <div className="navbar-links">
                        <button
                            className={`navbar-link${!informationPage && !isAdminPanelOpen && !isUserPanelOpen && !isQuizAuthorPageOpen && !isTodayScoresPageOpen ? " active" : ""}`}
                            type="button"
                            aria-current={!informationPage && !isAdminPanelOpen && !isUserPanelOpen && !isQuizAuthorPageOpen && !isTodayScoresPageOpen
                                ? "page"
                                : undefined}
                            onClick={() => {
                                setIsAdminPanelOpen(false);
                                setIsUserPanelOpen(false);
                                setIsQuizAuthorPageOpen(false);
                                setIsTodayScoresPageOpen(false);
                                setInformationPage(null);
                            }}
                        >
                            {text.quizNavigation}
                        </button>
                        <button
                            className={`navbar-link${!informationPage && isUserPanelOpen ? " active" : ""}`}
                            type="button"
                            aria-current={!informationPage && isUserPanelOpen ? "page" : undefined}
                            onClick={() => {
                                setIsAdminPanelOpen(false);
                                setIsUserPanelOpen(true);
                                setIsQuizAuthorPageOpen(false);
                                setIsTodayScoresPageOpen(false);
                                setInformationPage(null);
                            }}
                        >
                            {text.userPanelButton}
                        </button>
                        <button
                            className={`navbar-link${!informationPage && isTodayScoresPageOpen ? " active" : ""}`}
                            type="button"
                            aria-current={!informationPage && isTodayScoresPageOpen ? "page" : undefined}
                            onClick={() => {
                                setIsAdminPanelOpen(false);
                                setIsUserPanelOpen(false);
                                setIsQuizAuthorPageOpen(false);
                                setIsTodayScoresPageOpen(true);
                                setInformationPage(null);
                            }}
                        >
                            {text.todayScoresPageLabel}
                        </button>
                        {user.isAdmin && (
                            <button
                                className={`navbar-link${!informationPage && isAdminPanelOpen ? " active" : ""}`}
                                type="button"
                                aria-current={!informationPage && isAdminPanelOpen ? "page" : undefined}
                                onClick={() => {
                                    setIsUserPanelOpen(false);
                                    setIsAdminPanelOpen(true);
                                    setIsQuizAuthorPageOpen(false);
                                    setIsTodayScoresPageOpen(false);
                                    setInformationPage(null);
                                }}
                            >
                                {text.adminPanelButton}
                            </button>
                        )}
                        {(user.isAdmin || user.canCreateQuizzes) && (
                            <button
                                className={`navbar-link${!informationPage && isQuizAuthorPageOpen ? " active" : ""}`}
                                type="button"
                                aria-current={!informationPage && isQuizAuthorPageOpen ? "page" : undefined}
                                onClick={() => {
                                    setIsAdminPanelOpen(false);
                                    setIsUserPanelOpen(false);
                                    setIsQuizAuthorPageOpen(true);
                                    setIsTodayScoresPageOpen(false);
                                    setInformationPage(null);
                                }}
                            >
                                {text.createQuizButton}
                            </button>
                        )}
                    </div>
                )}
                <button
                    className="theme-toggle"
                    type="button"
                    onClick={toggleTheme}
                    aria-label={theme === "light" ? text.switchToDark : text.switchToLight}
                    aria-pressed={theme === "dark"}
                >
                    {theme === "light" ? text.darkMode : text.lightMode}
                </button>
                <button
                    className="language-toggle"
                    type="button"
                    onClick={toggleLanguage}
                    aria-label={language === "en" ? text.switchToArabic : text.switchToEnglish}
                >
                    {language === "en" ? text.switchToArabic : text.switchToEnglish}
                </button>
                {user && (
                    <div className="navbar-account">
                        <span className="signed-in-user">{user.name}</span>
                        <button className="navbar-logout" type="button" onClick={handleLogout}>
                            {text.logoutButton}
                        </button>
                    </div>
                )}
            </nav>
            {isCheckingSession ? (
                <main className="auth-page">
                    <p className="quiz-empty-state" role="status">{text.checkingSession}</p>
                </main>
            ) : sessionError && !user ? (
                <main className="auth-page">
                    <p className="quiz-empty-state" role="alert">
                        {text.authConnectionError} {sessionError}
                    </p>
                </main>
            ) : user ? (
                <>
                    {sessionError && <p className="auth-inline-error" role="alert">{sessionError}</p>}
                    {informationPage ? (
                        <InformationPage
                            page={informationPage}
                            language={language}
                            text={text}
                        />
                    ) : isAdminPanelOpen && user.isAdmin ? (
                        <AdminPanelPage text={text} />
                    ) : isQuizAuthorPageOpen && (user.isAdmin || user.canCreateQuizzes) ? (
                        <QuizAuthorPage
                            text={text}
                            onBack={() => setIsQuizAuthorPageOpen(false)}
                            onCreated={() => {
                                setIsQuizAuthorPageOpen(false);
                                setSessionError("");
                            }}
                        />
                    ) : isUserPanelOpen ? (
                        <UserPanelPage
                            user={user}
                            onUserUpdated={setUser}
                            text={text}
                            onBack={() => setIsUserPanelOpen(false)}
                        />
                    ) : isTodayScoresPageOpen ? (
                        <TodayScoresPage text={text} />
                    ) : (
                        <QuizesPage language={language} text={text} user={user} />
                    )}
                </>
            ) : (
                informationPage ? (
                    <InformationPage
                        page={informationPage}
                        language={language}
                        text={text}
                    />
                ) : (
                    <AuthPage text={text} onAuthenticated={setUser} />
                )
            )}
            <footer className="site-footer">
                <nav aria-label={text.footerNavigation}>
                    <button
                        type="button"
                        onClick={() => setInformationPage(
                            informationPage === "about" ? null : "about"
                        )}
                    >
                        {text.aboutLink}
                    </button>
                    <button
                        type="button"
                        onClick={() => setInformationPage(
                            informationPage === "support" ? null : "support"
                        )}
                    >
                        {text.supportLink}
                    </button>
                </nav>
                <span>{text.footerCopyright.replace("{year}", String(new Date().getFullYear()))}</span>
            </footer>
        </div>
    );
}

export default App;
